import { UserType } from "../interfaces/management/schema.interface";
import { Coord } from "../interfaces/meta.interface";
import { parse, parseCIDR } from "ipaddr.js"

/** A CIDR prefix parsed into an [address, prefixLength] pair by ipaddr.js. */
export type ParsedNetwork = ReturnType<typeof parseCIDR>;

/**
 * Parse CIDR prefix strings (IPv4/IPv6) into ipaddr.js networks, skipping any malformed entry
 * so a bad prefix can never break classification. Used to prepare the trusted-network list the
 * Classifier is constructed with.
 *
 * @param {string[]} prefixes - CIDR prefixes, e.g. from `microsoft.m365_ip_ranges`.
 * @returns {ParsedNetwork[]} - the successfully parsed networks.
 */
export function parseNetworks(prefixes: string[]): ParsedNetwork[] {
    return prefixes.flatMap((cidr) => {
        try {
            return [parseCIDR(cidr)];
        } catch {
            return [];
        }
    });
}

/**
 * Check if an IP address falls inside any of the given parsed networks (IPv4 or IPv6). Used by
 * the Classifier to recognise Microsoft 365 service IPs from the trusted ranges it was built with.
 *
 * @param {string} ipAddress - The IP address to check.
 * @param {ParsedNetwork[]} networks - Parsed CIDR networks (see parseNetworks).
 * @returns {boolean} - true if the IP is inside one of the networks.
 */
export function isIpInNetworks(ipAddress: string, networks: ParsedNetwork[]): boolean {
    try {
        const ip = parse(ipAddress);
        return networks.some((network) => ip.kind() === network[0].kind() && ip.match(network));
    } catch {
        return false; // unparseable input is never treated as a match
    }
}

/**
 * Check if an IP address is within a network range.
 *
 * @param {string} ipAddress - The IP address to check.
 * @param {string} networkAddress - The network address in CIDR notation (e.g., "192.168.1.0/24").
 * @returns {boolean} - true if the IP address is within the network range, false otherwise.
 */
export function isIPInNetwork(ipAddress: string, networkAddress: string): boolean {

    if (typeof ipAddress !== "string" || typeof networkAddress !== "string") {
        return false
    }

    const ip = parse(ipAddress);

    // if rule is for network range
    if (networkAddress.includes("/")) {
        const network = parseCIDR(networkAddress);
        // if IP address and network are of the same kind
        if (ip.kind() === network[0].kind()) {
            return ip.match(network); // check IP in network
        } else {
            return false; // IP types are different
        }
    } else {
        // if rule is for a single IP
        return ipAddress === networkAddress;
    }

}

/**
 * Check if an IP address is public or reserved.
 *
 * @param {string} ipAddress - The IP address to check.
 * @returns {boolean} - true if the IP address is public, false if it is reserved.
 */
export function isIPPublic(ipAddress: string): boolean {
    const ip = parse(ipAddress);
    return ip.range() === "unicast";
}

/**
 * Check if a point is within a circle.
 *
 * @param {Coord} checkPoint - The point to check.
 * @param {Coord} centerPoint - The center point of the circle.
 * @param {number} radius - The radius of the circle in meters.
 * @returns {boolean} - true if the point is within the circle, false otherwise.
 */
export function isPointInCircle(checkPoint: Coord, centerPoint: Coord, radius: number): boolean {
    const ky = 40000 / 360;
    const kx = Math.cos((Math.PI * centerPoint.lat) / 180.0) * ky;
    const dx = Math.abs(centerPoint.lng - checkPoint.lng) * kx;
    const dy = Math.abs(centerPoint.lat - checkPoint.lat) * ky;
    return Math.sqrt(dx * dx + dy * dy) <= radius / 1000;
}

/**
 * Check if a user is a system user.
 *
 * @param {number} user_type - The user type.
 * @returns {boolean} - true if the user is a system user, false otherwise.
 */
export function isSystemUser(user_type: number): boolean {

    // known user types
    const SystemUser: { [key in UserType]: boolean } = {
        0: false,
        1: false,
        2: false,
        3: true,
        4: true,
        5: true,
        6: true,
        7: true,
        8: true,
        99: true
    };

    return SystemUser[user_type as UserType] ?? false; // return false if unknown user type
}

/**
 * One recipient entry of an inbox rule's ForwardTo, RedirectTo or ForwardAsAttachmentTo value, as
 * {@link readForwardingRecipients} reads it:
 * - `smtp`: every `@` in the entry belongs to an SMTP address the reader found; `domains` holds each address's
 *   domain, normalised by {@link normaliseDomain}.
 * - `directory`: the entry has no `@` at all, so it names a recipient rather than an address — a display name, an
 *   alias, or a legacyExchangeDN (`/o=ExchangeLabs/…`, bare or as OWA's `"Name" [EX:/o=…]`).
 * - `unreadable`: the entry has an `@` that belongs to no address the reader found — an IP-literal domain
 *   (`a@[203.0.113.7]`), a second `@` (`a@ours.com@evil.example`), or a quoted local part
 *   (`"ceo@ours.com"@evil.example`). In the last two the reader would find only the inner, company-looking address
 *   and miss the domain the mail is actually sent to, so the entry is not read as an address at all.
 */
export type ForwardingRecipient = { kind: 'smtp'; domains: string[] } | { kind: 'directory' } | { kind: 'unreadable' };

/**
 * An SMTP address inside a recipient entry, capturing its domain. Neither part may hold whitespace, a quote, a
 * bracket, `:`, `;`, `,` or a second `@`, so the address is found inside each spelling the audit log uses — bare
 * (`a@b.com`), prefixed (`SMTP:a@b.com`, `smtp:a@b.com`) and OWA's `"Display Name" [SMTP:a@b.com]` — and text around
 * it, such as a quoted display name with a comma in it, cannot become part of the domain. Each match holds exactly
 * one `@`, which is how {@link readForwardingRecipients} tells that every `@` in an entry was read.
 */
const SMTP_ADDRESS = /[^\s"<>()[\]{}:;,@]+@([^\s"'<>()[\]{}:;,@]+)/g;

/**
 * A domain as the inside/outside-the-organisation comparison reads it: trimmed, lower-cased (DNS names ignore case,
 * and the audit log keeps whatever case the rule's author typed) and without a trailing `.` (the fully-qualified
 * spelling of the same name).
 *
 * @param {string} domain - A domain from an address, the tenant's domain list or the audit record's organisation name.
 * @returns {string} - The domain in its comparable form.
 */
export function normaliseDomain(domain: string): string {
    return domain.trim().toLowerCase().replace(/\.+$/, '');
}

/**
 * Read an inbox rule's forwarding value into its recipient entries. The audit log joins several recipients into one
 * value with `;` (`a@x.com;b@y.com`), so the value is split there; spaces around an entry, and empty entries
 * (`a@x.com; ;b@y.com;`), are ignored. An entry is read as addresses only when every `@` in it belongs to one.
 *
 * @param {string} value - A ForwardTo, RedirectTo or ForwardAsAttachmentTo value, as the audit record holds it.
 * @returns {ForwardingRecipient[]} - One reading per non-empty entry, in order; none for a value of separators and
 * spaces alone.
 */
export function readForwardingRecipients(value: string): ForwardingRecipient[] {
    return value
        .split(';')
        .map((entry) => entry.trim())
        .filter((entry) => entry !== '')
        .map((entry): ForwardingRecipient => {
            const ats = entry.split('@').length - 1;
            if (ats === 0) {
                return { kind: 'directory' };
            }
            const domains = Array.from(entry.matchAll(SMTP_ADDRESS), (match) => normaliseDomain(match[1]!));
            return domains.length === ats ? { kind: 'smtp', domains } : { kind: 'unreadable' };
        });
}

/**
 * Whether a value is a bare GUID, which is how a record's `Actor` and `Target` arrays carry application and object ids.
 *
 * @param value the candidate
 * @returns true for the 8-4-4-4-12 hexadecimal form, any letter case
 */
export function isGuid(value: unknown): value is string {
    return typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}
